import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getAuthFromLocalStorage } from './authUtils';
import { clearStorageKeys, clearStorageByPattern } from './storageUtils';
import { STORAGE_KEYS, isTerminalError } from '../sessionConfig';

// Create axios instance
const axiosInstance = axios.create();

// Track if interceptors have been configured to avoid duplicates
let interceptorsConfigured = false;

// Store the login route for redirects
let configuredLoginRoute = '/bbweb/login';

/**
 * Configure axios interceptors for authentication
 * This is called internally by SessionManager after config is loaded
 */

/**
 * Token refresh handlers for different providers
 * These will be set by the useTokenRefresh hook
 */
let auth0RefreshHandler = null;
let oidcRefreshHandler = null;
let gcpRefreshHandler = null;
let currentProvider = null; // Track which provider is actually being used

// Circuit breaker: once a terminal error (e.g., invalid_grant) is detected,
// stop ALL refresh attempts to prevent infinite retry loops between the
// pre-request interceptor and the 401 handler.
let terminalErrorDetected = false;

/**
 * Set token refresh handlers (called by useTokenRefresh hook)
 */
export const setTokenRefreshHandlers = (auth0Handler, oidcHandler, provider, gcpHandler) => {
  auth0RefreshHandler = auth0Handler;
  oidcRefreshHandler = oidcHandler;
  currentProvider = provider;
  if (gcpHandler !== undefined) gcpRefreshHandler = gcpHandler;
  // Reset circuit breaker when handlers are re-set (e.g., new session after login)
  if (auth0Handler || oidcHandler || gcpHandler) {
    terminalErrorDetected = false;
  }
};

/**
 * Get the current token refresh handler for the active provider.
 * Used by fetchA to share the same refresh logic.
 * @returns {Function|null} The refresh handler function or null if not configured
 */
export const getTokenRefreshHandler = () => {
  if (currentProvider === 'auth0' && auth0RefreshHandler) return auth0RefreshHandler;
  if (currentProvider === 'gcp' && gcpRefreshHandler) return gcpRefreshHandler;
  const isOIDC =
    currentProvider === 'aws' ||
    currentProvider === 'cognito' ||
    currentProvider === 'oidc';
  if (isOIDC && oidcRefreshHandler) return oidcRefreshHandler;
  return null;
};

/**
 * Get the configured login route for redirects.
 * @returns {string} The login route
 */
export const getLoginRoute = () => configuredLoginRoute;

export const getCurrentProvider = () => currentProvider;

/**
 * Check if a token is expired
 * @param {string} token - The JWT access token
 * @param {number} bufferSeconds - Buffer time before actual expiry (default 30 seconds)
 * @returns {boolean} True if the token is expired or will expire within the buffer time
 */
const isTokenExpired = (token, bufferSeconds = 30) => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);
    if (!decoded?.exp) return true;

    const expiresAtMs = decoded.exp * 1000;
    const now = Date.now();
    const bufferMs = bufferSeconds * 1000;

    return now >= expiresAtMs - bufferMs;
  } catch (error) {
    console.error('[bbAxios] Error decoding token:', error);
    return true;
  }
};

/**
 * Get fresh token using appropriate provider
 * CRITICAL: Only uses the handler for the active provider to prevent interference
 */
const getRefreshToken = async () => {
  // Circuit breaker: don't attempt refresh if a terminal error was already detected
  if (terminalErrorDetected) {
    throw new Error('Token refresh blocked: terminal error previously detected');
  }

  try {
    // Use the provider that was set during initialization - strict provider check
    const isAuth0 = currentProvider === 'auth0';
    const isGCP = currentProvider === 'gcp';

    // GCP (Firebase) manages its own token storage — skip the OIDC localStorage check
    if (!isGCP) {
      const authData = getAuthFromLocalStorage();
      if (!authData) {
        throw new Error('No auth data available');
      }
    }
    const isOIDC =
      currentProvider === 'aws' ||
      currentProvider === 'cognito' ||
      currentProvider === 'oidc';

    if (isAuth0) {
      if (!auth0RefreshHandler) {
        throw new Error('Auth0 refresh handler not available');
      }
      return await auth0RefreshHandler();
    } else if (isGCP) {
      if (!gcpRefreshHandler) {
        throw new Error('GCP refresh handler not available');
      }
      return await gcpRefreshHandler();
    } else if (isOIDC) {
      if (!oidcRefreshHandler) {
        throw new Error('OIDC refresh handler not available');
      }
      return await oidcRefreshHandler();
    } else {
      throw new Error(`Unknown provider: ${currentProvider}`);
    }
  } catch (error) {
    // Check if this is a terminal error (invalid_grant, login_required, etc.)
    // and trip the circuit breaker to prevent further attempts
    if (isTerminalError(error)) {
      console.error(
        '[bbAxios] Terminal auth error detected, stopping all refresh attempts:',
        error.error || error.message
      );
      terminalErrorDetected = true;
    }
    console.error('Token refresh failed:', error);
    throw error;
  }
};

/**
 * Should the user's bearer token be attached to this request?
 *
 * Only for this application's own API. Some calls through this instance target other
 * services -- document-server most notably (forcesave, ConvertService) -- and those
 * have no business receiving a user credential. document-server writes the
 * Authorization header verbatim into its logs, so attaching it there put live tokens
 * for named customer users into a third-party service's log files.
 *
 * Compares hostname rather than full origin: every app endpoint is a relative path,
 * but local development serves some services on other ports of localhost, and those
 * are legitimately the same application.
 *
 * Fails closed -- an unparseable target does not get the token.
 */
export const shouldAttachAuth = (requestConfig) => {
  if (typeof window === 'undefined') return true;
  try {
    const base = requestConfig.baseURL
      ? new URL(requestConfig.baseURL, window.location.href).href
      : window.location.href;
    const target = new URL(requestConfig.url ?? '', base);
    return target.hostname === window.location.hostname;
  } catch {
    console.warn(
      '[bbAxios] could not resolve request URL; withholding Authorization header',
      requestConfig.url
    );
    return false;
  }
};

export const configureAxios = (loginRoute = '/bbweb/login') => {
  // Store the login route for later use
  configuredLoginRoute = loginRoute;

  // Prevent duplicate interceptor registration
  if (interceptorsConfigured) {
    return;
  }

  // Add request interceptor to attach authentication token
  axiosInstance.interceptors.request.use(
    async (requestConfig) => {
      let accessToken;

      if (currentProvider === 'gcp') {
        // GCP (Firebase) stores its ID token under a dedicated localStorage key
        accessToken = localStorage.getItem(STORAGE_KEYS.GCP_TOKEN);
        if (!accessToken) return requestConfig;
      } else {
        // Auth0 / OIDC: token is in the OIDC-managed localStorage entry
        const oidcAuth = getAuthFromLocalStorage();
        if (!oidcAuth) return requestConfig;
        accessToken = oidcAuth?.access_token;
      }

      // Check if token is expired or about to expire
      if (accessToken && isTokenExpired(accessToken)) {
        console.log(
          '[bbAxios] Token expired or expiring, attempting refresh before request'
        );
        try {
          // Try to refresh the token before making the request
          const newToken = await getRefreshToken();
          if (newToken) {
            accessToken = newToken;
          }
        } catch (refreshError) {
          console.error('[bbAxios] Pre-request token refresh failed:', refreshError);
          // If terminal error, don't send the request with the expired token —
          // it'll just 401 and trigger another doomed refresh attempt
          if (terminalErrorDetected) {
            return Promise.reject(refreshError);
          }
          // Continue with the old token, let the 401 handler deal with it
        }
      }

      if (accessToken && shouldAttachAuth(requestConfig)) {
        if (!requestConfig.headers) {
          requestConfig.headers = {};
        }
        requestConfig.headers['Authorization'] = `Bearer ${accessToken}`;
      }

      return requestConfig;
    },
    (error) => {
      console.error('[bbAxios REQUEST ERROR]', error);
      return Promise.reject(error);
    }
  );

  // Add response interceptor to handle errors
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;

      // Only handle 401 Unauthorized errors for token refresh
      // This is a safety net - OIDC should auto-refresh tokens before expiry
      if (status === 401 && !originalRequest._isRetryAfterTokenRefresh) {
        // Check if this is a permission/authorization error vs auth token error
        const errorData = error.response?.data;
        const isPermissionError =
          errorData?.error &&
          typeof errorData.error === 'string' &&
          (errorData.error.includes('not enabled') ||
            errorData.error.includes('permission'));

        // If it's a permission error (not a token issue), don't try to refresh
        if (isPermissionError) {
          console.warn('[bbAxios] Permission denied:', errorData.error);
          return Promise.reject(error);
        }

        // Mark to prevent infinite retry loops
        originalRequest._isRetryAfterTokenRefresh = true;

        try {
          // Attempt to get a fresh token
          const newToken = await getRefreshToken();

          if (!newToken) {
            console.error('Token refresh succeeded but no token returned');
            return Promise.reject(error);
          }

          // Update the request with new token
          if (!originalRequest.headers) {
            originalRequest.headers = {};
          }
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

          // Retry the original request once with the new token
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed during 401 retry:', refreshError);
          // Redirect to login on refresh failure, preserving current path for post-login redirect
          clearStorageKeys(Object.values(STORAGE_KEYS));
          clearStorageByPattern('oidc.');
          clearStorageByPattern('@@auth0spajs@@');
          const returnUrl = window.location.pathname + window.location.search;
          const separator = configuredLoginRoute.includes('?') ? '&' : '?';
          window.location.href = `${configuredLoginRoute}${separator}redirect=${encodeURIComponent(returnUrl)}`;
          return Promise.reject(refreshError);
        }
      }

      // For server errors (5xx), log but don't retry
      if (status >= 500) {
        console.error(
          `Server error ${status} for ${originalRequest.url}:`,
          error.message
        );
      }

      // Handle 403 Forbidden - permission denied (valid token, but user lacks required role)
      // This should NOT trigger logout - user is authenticated, just not authorized for this resource
      if (status === 403) {
        const errorData = error.response?.data;
        console.warn(
          `[bbAxios] Permission denied for ${originalRequest.url}:`,
          errorData?.error || 'insufficient permissions',
          errorData?.requiredRoles
            ? `(requires: ${errorData.requiredRoles.join(', ')})`
            : ''
        );
        // Don't logout - just reject so the component can handle the error
        return Promise.reject(error);
      }

      // Dispatch quota exceeded event for 429 responses with QUOTA_EXCEEDED code
      if (status === 429 && error.response?.data?.code === 'QUOTA_EXCEEDED') {
        window.dispatchEvent(new CustomEvent('quotaExceeded', { detail: error.response.data }));
      }

      // For client errors other than 401 and 403, log but don't retry
      if (status >= 400 && status < 500 && status !== 401 && status !== 403) {
        console.error(
          `Client error ${status} for ${originalRequest.url}:`,
          error.message
        );
      }

      return Promise.reject(error);
    }
  );

  interceptorsConfigured = true;
};

export default axiosInstance;
